import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2, XCircle } from "lucide-react";

const MODULE_LABELS = {
  cours_prive: "Lien cours privé", sinistre_assureur: "Dossier → assureur",
  sinistre_locataire: "Dossier → locataire",
};

export default function CourrielsEnvoyes() {
  const [data, setData] = useState({ emails: [], total: 0, sent: 0, failed: 0 });
  const [toEmail, setToEmail] = useState("");
  const [studentName, setStudentName] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/sent-emails"); setData(data); }
    catch (e) { toast.error("Erreur de chargement"); }
  };
  useEffect(() => { load(); }, []);

  const sendLink = async () => {
    if (!toEmail.trim()) { toast.error("Entre l'adresse courriel"); return; }
    setSending(true);
    try {
      await api.post("/sent-emails/private-lesson-link", { to_email: toEmail.trim(), student_name: studentName.trim() });
      toast.success(`Lien cours privé envoyé à ${toEmail} ✓`);
      setToEmail(""); setStudentName("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'envoi"); }
    finally { setSending(false); }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

  return (
    <div className="p-6 text-zinc-100 max-w-5xl mx-auto" data-testid="courriels-envoyes">
      <h1 className="text-3xl font-bold flex items-center gap-2"><Mail className="text-emerald-400" /> Courriels envoyés</h1>
      <p className="text-zinc-400 text-sm mt-1">Journal des courriels transactionnels envoyés (dossier de sinistre, liens cours privé, etc.) avec leur statut de livraison.</p>

      <div className="rounded-xl border border-sky-800/60 bg-sky-950/20 p-4 mt-5 space-y-3" data-testid="send-private-link-box">
        <div className="text-sm font-semibold text-sky-300 flex items-center gap-2"><Send size={16} /> Envoyer le lien de cours privé à un contact</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="Courriel du parent / prospect"
            className="bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm sm:flex-1" data-testid="private-link-email" />
          <input type="text" value={studentName} onChange={(e) => setStudentName(e.target.value)} placeholder="Nom de l'élève (optionnel)"
            className="bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm sm:w-56" data-testid="private-link-name" />
          <button onClick={sendLink} disabled={sending} data-testid="private-link-send-btn"
            className="flex items-center justify-center gap-2 bg-sky-600 text-white font-semibold px-5 py-2 rounded-lg hover:bg-sky-500 disabled:opacity-50 whitespace-nowrap">
            <Send size={15} /> {sending ? "Envoi…" : "Envoyer le lien"}</button>
        </div>
      </div>

      <div className="flex gap-4 mt-5">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3"><div className="text-2xl font-bold text-emerald-400" data-testid="stat-sent">{data.sent}</div><div className="text-xs text-zinc-400">envoyés</div></div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3"><div className="text-2xl font-bold text-red-400" data-testid="stat-failed">{data.failed}</div><div className="text-xs text-zinc-400">échecs</div></div>
      </div>

      <div className="mt-4 rounded-xl border border-zinc-800 overflow-x-auto">
        <table className="w-full text-sm" data-testid="sent-emails-table">
          <thead className="bg-zinc-900 text-zinc-400"><tr>
            <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Destinataire</th>
            <th className="text-left px-3 py-2">Sujet</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Statut</th>
          </tr></thead>
          <tbody>
            {data.emails.map((e) => (
              <tr key={e.id} className="border-t border-zinc-800" data-testid={`email-row-${e.id}`}>
                <td className="px-3 py-2 text-zinc-400 whitespace-nowrap">{fmt(e.created_at)}</td>
                <td className="px-3 py-2">{(e.to || []).join(", ")}{e.cc?.length ? <span className="text-zinc-500 text-xs"> · cc {e.cc.join(", ")}</span> : null}</td>
                <td className="px-3 py-2 text-zinc-300 max-w-[280px] truncate">{e.subject}</td>
                <td className="px-3 py-2 text-xs text-zinc-400">{MODULE_LABELS[e.module] || e.module}</td>
                <td className="px-3 py-2">{e.status === "sent"
                  ? <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 size={14} /> Envoyé</span>
                  : <span className="text-red-400 flex items-center gap-1" title={e.error}><XCircle size={14} /> Échec</span>}</td>
              </tr>
            ))}
            {data.emails.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-zinc-500">Aucun courriel envoyé pour l'instant.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
